
import "./PlayerControls.css";
import React, { FC, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { ApplicationState } from "../../../machinelogic/constants";
import { FeatureFlags } from "@vse/featureFlags";
import { ActionButton, ButtonActions } from "./ActionButton";
import type { Library, ApplicationBase } from '../../../machinelogic/types';

interface PlayerControlsProps {
    library: Library;
    isRobotReady: (application: ApplicationBase) => boolean;
    runTimeConfig?: unknown;
    smallButtonSize: boolean;
}


export const PlayerControls: FC<PlayerControlsProps> = observer(({ library, isRobotReady, runTimeConfig, smallButtonSize }) => {
  //isPlayRequestInFlight is a boolean for if we made a play request
  //showSpinner is a boolean that determines if we should display the spinner depending on how long we have been waiting for the play request to complete
  const [isPlayRequestInFlight, setIsPlayRequestInFlight] = useState<boolean>(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  const currentApp = library.currentApplication;
  let parentClassName = "player-control";
  if(smallButtonSize){
    parentClassName += " " + "small";
  }

  useEffect(() => {
    // only show spinner if the play request request takes too long to complete
    if (isPlayRequestInFlight){
      const showSpinnerTimeout = setTimeout(()=>{
        setShowSpinner(true);
      },200);
      return () => { clearTimeout(showSpinnerTimeout); };
    }
    else{
      setShowSpinner(false);
    }
  });

  function handleClick(event:React.MouseEvent<HTMLButtonElement>, action:ButtonActions): void{
    const KEYBOARD_KEY_PRESS = 0;
    if (event.detail === KEYBOARD_KEY_PRESS) {
      event.preventDefault();
    }

    if (!isRobotReady(currentApp)) {
      return;
    }

    if (action === ButtonActions.PLAY) {
      setIsPlayRequestInFlight(true);
      currentApp.play(runTimeConfig, () => {

        const robotAppsService =  currentApp.mLibrary.getRobotAppsService();
        robotAppsService.send('MachineLogic/PlayerControl/onClick');

        setIsPlayRequestInFlight(false);
      });
    }
    else if (action === ButtonActions.PAUSE) {
      currentApp.pause();
    }
    else if (action === ButtonActions.RESUME) {
      currentApp.resume();
    }
    else if (action === ButtonActions.STOP) {
      currentApp.stop();
    }
  }

  function renderPlayerView(): React.ReactElement{
    if(!currentApp) {
      return(
        <>
          <ActionButton
            action={ButtonActions.PLAY}
            isDisabled={true}
            handleClick={handleClick}/>
          <ActionButton
            action={ButtonActions.STOP}
            isDisabled={true}
            handleClick={handleClick}/>
        </>
      );
    }

    if (currentApp.mState == ApplicationState.RUNNING){
      return(
        <>
          {FeatureFlags.HIDE_PAUSE_BUTTON ?
            <ActionButton
              action={ButtonActions.PLAY}
              isDisabled={true}
              handleClick={handleClick}
            /> :
            <ActionButton
              action={ButtonActions.PAUSE}
              isDisabled={false}
              handleClick={handleClick}
            />}
          <ActionButton action={ButtonActions.STOP}
            isDisabled={false}
            handleClick={handleClick}
          />
        </>
      );
    }
    else if (currentApp.mState == ApplicationState.PAUSED){
      return(
        <>
          <ActionButton
            action={ButtonActions.RESUME}
            isDisabled={isPlayRequestInFlight}
            showSpinner={showSpinner}
            handleClick={handleClick}
          />
          <ActionButton
            action={ButtonActions.STOP}
            isDisabled={false}
            handleClick={handleClick}
          />
        </>
      );
    }
    else{
      return(
        <>
          <ActionButton
            action={ButtonActions.PLAY}
            isDisabled={isPlayRequestInFlight}
            showSpinner={showSpinner}
            handleClick={handleClick}/>
          <ActionButton
            action={ButtonActions.STOP}
            isDisabled={true}
            handleClick={handleClick}/>
        </>
      );
    }
  }

  return (
    <div className={parentClassName}>
      {renderPlayerView()}
    </div>
  );
});
